import { Avatar, Spinner, TypeBadge } from '@/components/ui';
import { useSprintSummary } from '@/hooks/queries';
import { formatDateRange } from '@/lib/design';
import { useEffect } from 'react';

/**
 * Resumen de un sprint cerrado.
 *
 * Lo que se lista es exactamente lo entregado: al cerrar el sprint, lo que
 * quedo sin terminar sale de el. Por eso lo pendiente se cuenta en puntos
 * (a partir del compromiso congelado) pero no se puede detallar tarea a tarea.
 */
export function SprintSummaryDrawer({
  sprintId,
  onClose,
}: {
  sprintId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useSprintSummary(sprintId);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-40 flex justify-end bg-[rgba(4,4,10,.62)]"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={data ? `Resumen de ${data.sprint.name}` : 'Resumen del sprint'}
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-[560px] max-w-full flex-col border-l border-line bg-surface shadow-[-16px_0_40px_rgba(0,0,0,.5)]"
      >
        {isLoading || !data ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <header className="flex flex-none items-start gap-3 border-b border-line px-[22px] py-4">
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-lg font-semibold">{data.sprint.name}</h2>
                <p className="mt-0.5 text-[12.5px] text-subtle">
                  {data.sprint.project.name} ·{' '}
                  {formatDateRange(data.sprint.startDate, data.sprint.endDate)}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="flex size-[30px] flex-none items-center justify-center rounded-control border border-line-strong transition-colors hover:bg-muted-surface"
              >
                ✕
              </button>
            </header>

            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto px-[22px] py-5">
              {data.sprint.goal && (
                <section>
                  <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[.1em] text-faint">
                    Objetivo
                  </h3>
                  <p className="text-[13.5px] leading-relaxed text-body-soft text-pretty">
                    {data.sprint.goal}
                  </p>
                </section>
              )}

              <section className="grid grid-cols-4 gap-3">
                <Stat label="Comprometido" value={data.sprint.committedPoints} unit="pts" />
                <Stat label="Completado" value={data.sprint.completedPoints} unit="pts" />
                <Stat label="Sin terminar" value={data.carriedOverPoints} unit="pts" />
                <Stat label="Cumplimiento" value={data.completionRate} unit="%" />
              </section>

              {data.sprint.committedPoints === null && (
                <p className="rounded-control border border-warn-line bg-warn-soft px-3 py-2 text-[12.5px] text-warn-text text-pretty">
                  Este sprint se cerro antes de que se guardaran sus cifras, asi que solo se
                  conserva lo que quedo dentro. Los sprints que cierres a partir de ahora traen el
                  historial completo.
                </p>
              )}

              {data.byPerson.length > 0 && (
                <section>
                  <h3 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[.1em] text-faint">
                    Quien entrego que
                  </h3>
                  <ul className="flex flex-col gap-2">
                    {data.byPerson.map((row) => (
                      <li
                        key={row.user?.id ?? 'sin-asignar'}
                        className="flex items-center gap-2.5 rounded-control border border-line bg-sunken px-3 py-2"
                      >
                        <Avatar user={row.user} size={26} />
                        <span className="min-w-0 flex-1 truncate text-[13.5px]">
                          {row.user?.fullName ?? 'Sin asignar'}
                        </span>
                        <span className="text-[12.5px] text-subtle">
                          {row.items} {row.items === 1 ? 'tarea' : 'tareas'}
                        </span>
                        <span className="points-badge">{row.points}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section>
                <h3 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[.1em] text-faint">
                  Entregado ({data.items.length})
                </h3>
                {data.items.length === 0 ? (
                  <p className="text-[13px] text-faint">Este sprint se cerro sin nada terminado.</p>
                ) : (
                  <ul className="overflow-hidden rounded-panel border border-line">
                    {data.items.map((item) => (
                      <li
                        key={item.id}
                        className="flex items-center gap-3 border-b border-line-soft px-3 py-2.5 last:border-b-0"
                      >
                        <TypeBadge type={item.type} />
                        <span className="key-badge w-16 flex-none">{item.key}</span>
                        <span className="min-w-0 flex-1 truncate text-[13px]">{item.title}</span>
                        {item.storyPoints !== null && (
                          <span className="points-badge">{item.storyPoints}</span>
                        )}
                        <Avatar user={item.assignee} size={22} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, unit }: { label: string; value: number | null; unit: string }) {
  return (
    <div className="rounded-control border border-line bg-sunken px-3 py-2.5">
      <p className="font-display text-[19px] font-semibold leading-none">
        {value === null ? '—' : `${value}${unit === '%' ? '%' : ''}`}
        {value !== null && unit !== '%' && (
          <span className="ml-1 text-[12px] font-normal text-subtle">{unit}</span>
        )}
      </p>
      <p className="mt-1.5 text-[11px] text-subtle">{label}</p>
    </div>
  );
}
